import { IconCheckSquare, IconPlus } from '@maia/icons';
import {
  ButtonLink,
  DropdownOption,
  DropdownOptionAvatar,
  DropdownOptionStatus,
  DropdownSelectedAvatarValue,
  DropdownSelectedStatusValue,
  DropdownSelectedValue,
  FormField,
  PageHeader,
  Select,
  Typography,
} from '@maia/ui';
import { useForm } from '@tanstack/react-form';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import { GetBrokers } from '../../../features/broker/get-brokers/queryOptions';
import { getSelf } from '../../../features/broker/get-self/queryOptions';
import { getTeams } from '../../../features/broker/get-teams/queryOptions';
import { TasksList } from '../../../features/task/tasks/list';
import { getTasksOptions } from '../../../features/task/tasks/queryOptions';
import { useFilter } from '../../../utils/filter/filter';
import { validateSearch } from '../../../utils/pagination';
import { match } from 'ts-pattern';
import { mapInitialFilters } from '../../../utils/filter/utils';
import { useUpdateOptions } from '../../../features/task/tasks/queryOptions';
import { TaskDto } from '@maia/shared';
import { twMerge } from 'tailwind-merge';

export const Route = createFileRoute('/_authenticated/tasks/')({
  component: Page,
  loaderDeps: ({ search }) => search,
  loader: async ({ context: { queryClient }, deps: search }) => {
    const me = await queryClient.ensureQueryData(getSelf());
    const tasks = await queryClient.ensureQueryData(
      getTasksOptions(mapInitialFilters(search)),
    );
    return { tasks, me };
  },
  validateSearch,
});

function Page() {
  const { tasks: initialTasks, me } = Route.useLoaderData();
  const { state, dispatch: setSearch, mappedState } = useFilter('/tasks');
  const { data: brokers } = useSuspenseQuery(GetBrokers());

  const setLimit = useCallback(
    (payload: 10 | 20 | 50) => setSearch({ type: 'setLimit', payload }),
    [setSearch],
  );

  const setPage = useCallback(
    (payload: number) => setSearch({ type: 'paginate', payload }),
    [setSearch],
  );

  const setFilter = useCallback(
    (value: string) =>
      setSearch({ type: 'setFilter', payload: ['filter', value] }),
    [setSearch],
  );

  const setSorting = useCallback(
    (value: string) => {
      const payload = match(value)
        .with('most_recent', () => [{ id: 'recent', desc: false }])
        .with('highest_priority', () => [{ id: 'priority', desc: false }])
        .with('lowest_priority', () => [{ id: 'priority', desc: true }])
        .with('duration', () => [{ id: 'duration', desc: false }])
        .otherwise(() => []);
      return setSearch({ type: 'setSort', payload });
    },
    [setSearch],
  );

  const { data: tasks } = useQuery({
    ...getTasksOptions(mappedState),
    initialData: initialTasks,
  });

  const { mutate: updateOptions, isPending: isUpdating } = useUpdateOptions();

  const handleTaskUpdate = useCallback(
    (task: TaskDto) => {
      updateOptions({
        customerGuid: task.customer?.guid,
        taskGuid: task.guid,
        publiclyVisible: false,
        priority: task.priority.value,
        assignee: task.assignee?.guid,
        team: task.team.guid,
        status: task.status.value,
      });
    },
    [tasks, updateOptions],
  );

  return (
    <div className="layout-sidebar">
      <PageHeader
        className="area-page-header"
        icon={IconCheckSquare}
        title="Taken"
        actions={
          <ButtonLink
            intent="cta"
            size="default"
            iconLeft={IconPlus}
            to="/tasks/new"
          >
            Nieuwe taak
          </ButtonLink>
        }
      />
      <div className="area-page-content content-grid">
        <div
          className={twMerge(
            'col-span-full flex items-center justify-between',
            isUpdating &&
              'pointer-events-none opacity-25 transition-opacity duration-200',
          )}
        >
          <TasksList
            tasks={tasks}
            brokers={brokers}
            me={me}
            setLimit={setLimit}
            setPage={setPage}
            setFilter={setFilter}
            setSorting={setSorting}
            activeFilter={state.filter?.find(([key]) => key === 'filter')?.[1]}
            activeSorting={state.sort?.[0]}
            onBrokerChange={(task, broker) =>
              handleTaskUpdate({ ...task, assignee: broker })
            }
            onPriorityChange={(task, priority) => {
              const priorityValue =
                priorities.find((p) => p.value === priority) ?? task.priority;
              return handleTaskUpdate({
                ...task,
                priority: {
                  label: priorityValue.label,
                  value: priorityValue.value,
                  meta: priorityValue.meta,
                },
              });
            }}
            onStatusChange={(task, status) => {
              const statusValue =
                statuses.find((s) => s.value === status) ?? task.status;
              return handleTaskUpdate({
                ...task,
                status: {
                  label: statusValue.label,
                  value: statusValue.value,
                  meta: statusValue.meta,
                },
              });
            }}
          />
        </div>
      </div>
      <FilterSidebar setSearch={setSearch} />
    </div>
  );
}

type FilterSidebarProps = {
  setSearch: ReturnType<typeof useFilter>['dispatch'];
};

function FilterSidebar(props: FilterSidebarProps) {
  const { setSearch } = props;
  const { data: brokers } = useSuspenseQuery(GetBrokers());
  const { data: teams } = useSuspenseQuery(getTeams());

  const form = useForm({
    defaultValues: {
      status: undefined,
      team: undefined,
      type: undefined,
      priority: undefined,
      broker: undefined,
      createdby: undefined,
    },
  });

  return (
    <div className="area-page-sidebar flex flex-col gap-y-32">
      <Typography variant="display6" weight="medium">
        Filteren op
      </Typography>
      <form className="flex flex-col gap-y-24">
        <form.Field name="status">
          {(field) => (
            <FormField
              label="Status"
              inputSlot={
                <Select
                  enforceMaxHeight
                  onChange={(value) =>
                    setSearch({
                      type: 'setFilter',
                      payload: ['status', value.toString()],
                    })
                  }
                  placeholder="Selecteren"
                  items={statuses}
                />
              }
            />
          )}
        </form.Field>
        {/* <form.Field name="team">
          {(field) => (
            <FormField
              label="Team"
              inputSlot={
                <Select
                  onChange={(value) =>
                    setSearch({
                      type: 'setFilter',
                      payload: ['team', value.toString()],
                    })
                  }
                  placeholder="Selecteren"
                  items={[
                    {
                      value: '',
                      label: 'Alle',
                      meta: {},
                    },
                    ...teams.map((team) => ({
                      label: team.name,
                      value: team.guid,
                      meta: {},
                    })),
                  ]}
                />
              }
            />
          )}
        </form.Field> */}
        <form.Field name="type">
          {(field) => (
            <FormField
              label="Type"
              inputSlot={
                <Select
                  enforceMaxHeight
                  onChange={(value) =>
                    setSearch({
                      type: 'setFilter',
                      payload: ['type', value.toString()],
                    })
                  }
                  placeholder="Selecteren"
                  items={types}
                />
              }
            />
          )}
        </form.Field>
        <form.Field name="priority">
          {(field) => (
            <FormField
              label="Prioriteit"
              inputSlot={
                <Select
                  enforceMaxHeight
                  onChange={(value) =>
                    setSearch({
                      type: 'setFilter',
                      payload: ['priority', value.toString()],
                    })
                  }
                  placeholder="Selecteren"
                  items={priorities}
                  renderSelectedValue={(item) => {
                    if (!item.value) {
                      return (
                        <DropdownSelectedValue
                          label={item.label}
                          size="small"
                        />
                      );
                    }
                    return (
                      <DropdownSelectedStatusValue
                        status={item.meta}
                        label={item.label}
                      />
                    );
                  }}
                  renderItem={(item, itemInfo) => {
                    if (!item.value) {
                      return (
                        <DropdownOption label={item.label} {...itemInfo} />
                      );
                    }
                    return (
                      <DropdownOptionStatus
                        label={item.label}
                        status={item.meta}
                      />
                    );
                  }}
                />
              }
            />
          )}
        </form.Field>
        <form.Field name="broker">
          {() => (
            <FormField
              label="Broker"
              inputSlot={
                <Select
                  enforceMaxHeight
                  onChange={(value) =>
                    setSearch({
                      type: 'setFilter',
                      payload: ['broker', value.toString()],
                    })
                  }
                  placeholder="Selecteren"
                  items={[
                    {
                      value: '',
                      label: 'Alle',
                      meta: {},
                    },
                    ...brokers.map((broker) => ({
                      label: `${broker.firstName ?? broker.initials} ${broker.lastName}`,
                      value: broker.guid,
                      meta: {
                        initials: `${broker.initials}`,
                      },
                    })),
                  ]}
                  renderItem={(item, itemInfo) => {
                    if (!item.value) {
                      return (
                        <DropdownOption label={item.label} {...itemInfo} />
                      );
                    }
                    return (
                      <DropdownOptionAvatar
                        label={item.label}
                        variant="square"
                        avatar={{
                          initials: item.meta.initials,
                        }}
                      />
                    );
                  }}
                  renderSelectedValue={(item) => {
                    if (!item.value) {
                      return (
                        <DropdownSelectedValue
                          label={item.label}
                          size="small"
                        />
                      );
                    }
                    return (
                      <DropdownSelectedAvatarValue
                        label={item.label}
                        variant="square"
                        avatar={{ initials: item.meta.initials }}
                      />
                    );
                  }}
                />
              }
            />
          )}
        </form.Field>
        <form.Field name="createdby">
          {(field) => (
            <FormField
              label="Aangemaakt door"
              inputSlot={
                <Select
                  enforceMaxHeight
                  onChange={(value) =>
                    setSearch({
                      type: 'setFilter',
                      payload: ['created_by', value.toString()],
                    })
                  }
                  placeholder="Selecteren"
                  items={[
                    {
                      value: '',
                      label: 'Alle',
                      meta: {},
                    },
                    ...brokers.map((broker) => ({
                      label: `${broker.firstName ?? broker.initials} ${broker.lastName}`,
                      value: broker.guid,
                      meta: {
                        initials: `${broker.firstName[0] ?? broker.initials[0]}`,
                      },
                    })),
                  ]}
                  renderItem={(item, itemInfo) => {
                    if (!item.value) {
                      return (
                        <DropdownOption label={item.label} {...itemInfo} />
                      );
                    }
                    return (
                      <DropdownOptionAvatar
                        label={item.label}
                        variant="square"
                        avatar={{
                          initials: item.meta.initials[0],
                        }}
                      />
                    );
                  }}
                  renderSelectedValue={(item) => {
                    if (!item.value) {
                      return (
                        <DropdownSelectedValue
                          label={item.label}
                          size="small"
                        />
                      );
                    }
                    return (
                      <DropdownSelectedAvatarValue
                        label={item.label}
                        variant="square"
                        avatar={{ initials: item.meta.initials }}
                      />
                    );
                  }}
                />
              }
            />
          )}
        </form.Field>
      </form>
    </div>
  );
}

const statuses = [
  {
    value: '',
    label: 'Alle',
    meta: {},
  },
  {
    value: 'in_review',
    label: 'Open',
    meta: {},
  },
  {
    value: 'in_progress',
    label: 'In uitvoering',
    meta: {},
  },
  {
    value: 'closed',
    label: 'Afgerond',
    meta: {},
  },
  {
    value: 'postponed',
    label: 'In afwachting',
    meta: {},
  },
] as const;

const types = [
  {
    value: '',
    label: 'Alle',
    meta: {},
  },
  {
    value: 'claim',
    label: 'Claim',
    meta: {},
  },
  {
    value: 'insurance',
    label: 'Verzekering',
    meta: {},
  },
  {
    value: 'quotation',
    label: 'Offerte',
    meta: {},
  },
  {
    value: 'customer',
    label: 'Klant',
    meta: {},
  },
] as const;

const priorities = [
  {
    value: '',
    label: 'Alle',
    meta: {
      color: undefined,
      variant: 'square',
    },
  },
  {
    value: 'low',
    label: 'Laag',
    meta: {
      color: 'success',
      variant: 'square',
    },
  },
  {
    value: 'medium',
    label: 'Gemiddeld',
    meta: {
      color: 'info',
      variant: 'square',
    },
  },
  {
    value: 'high',
    label: 'Hoog',
    meta: {
      color: 'warning',
      variant: 'square',
    },
  },
  {
    value: 'critical',
    label: 'Kritiek',
    meta: {
      color: 'error',
      variant: 'square',
    },
  },
] as const;
